<template>
  <div :class="containerClasses">
    <label class="label" :for="id"> {{ label }} </label>
    <input
      :id="id"
      class="input"
      :type="type"
      :placeholder="placeholder"
      :value="modelValue"
      @input="(e) => $emit('update:modelValue', e.target.value)"
      @blur="onBlur"
    />
    <p class="extra-text error-text" v-if="shouldErrorShown">
      {{ errorText }}
    </p>
    <p class="extra-text caption" v-if="shouldCaptionShown">
      {{ caption }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { PropType, defineProps, defineEmits, computed, ref } from "vue";

const props = defineProps({
  id: {
    type: String,
    default: () => Math.random().toString(16),
  },
  type: {
    type: String as PropType<"text" | "date" | "time">,
    default: "text",
  },
  label: {
    type: String,
    default: "Label not set",
  },
  minLabelWidth: {
    type: Number,
    default: 0,
  },
  modelValue: {
    type: String,
    default: "",
  },
  errorText: {
    type: String,
  },
  caption: {
    type: String,
    default: "",
  },
  placeholder: String,
  validator: {
    type: Function as PropType<(value: string) => boolean>,
    default: () => true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const isValid = computed(() => props.validator(props.modelValue));
const minLabelWidthStyle = computed(() => `${props.minLabelWidth}px`);

const containerClasses = computed(() => ({
  ["input-container"]: true,
  error: !isValid.value && touched.value,
}));

const touched = ref(false);

const onBlur = (e: any) => {
  emit("update:modelValue", e.target?.value?.trim());
  touched.value = true;
};

const shouldErrorShown = computed(
  () => !isValid.value && touched.value && props.errorText
);
const shouldCaptionShown = computed(
  () => !shouldErrorShown.value && props.caption
);
</script>

<style scoped>
.input-container {
  margin-bottom: 30px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  &.error {
    .input {
      color: var(--md-sys-color-error);
      border-bottom: 1px solid var(--md-sys-color-error);
    }
  }

  .extra-text {
    position: absolute;
    bottom: -1.5rem;
    left: 0;
    margin-bottom: 0;
    font-size: 0.9rem;
  }

  .error-text {
    color: var(--md-sys-color-error);
  }

  .caption {
    color: var(--md-sys-color-secondary);
  }

  .label {
    flex-shrink: 0;
    margin-right: 8px;
    text-align: left;
    min-width: v-bind(minLabelWidthStyle);
    color: var(--md-sys-color-on-background);
  }

  .input {
    flex-grow: 1;
    border: none;
    background: transparent;
    border-bottom: 1px solid var(--md-sys-color-on-background);
    color: var(--md-sys-color-on-background);
    &:focus {
      outline: none;
    }
  }
}
</style>
